import { useTranslation } from "react-i18next";
import { type WizardSteps } from "@/stores/wizard-store";
import { Check, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const STEP_KEYS = ["brandBasics", "brandKit", "social", "team", "aiConfig"] as const;
const STEP_STORE_KEYS = ["basics", "brandKit", "social", "team", "aiConfig"] as const;

interface StepWelcomeProps {
  steps: WizardSteps;
  onStart: () => void;
  onSkipAll: () => void;
}

export function StepWelcome({ steps, onStart, onSkipAll }: StepWelcomeProps) {
  const { t } = useTranslation("wizard");

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-[22px] font-bold text-foreground tracking-tight leading-tight">
          {t("steps.welcome.title")}
        </h2>
        <p className="text-[14px] text-muted-foreground mt-1.5 leading-relaxed">
          {t("steps.welcome.description")}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {STEP_KEYS.map((key, i) => {
          const storeKey = STEP_STORE_KEYS[i];
          const status = steps[storeKey];
          const done = status === "complete" || status === "skipped";
          return (
            <div
              key={key}
              className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5"
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold flex-shrink-0 ${
                  done
                    ? "bg-[var(--success-subtle)] text-[var(--success)]"
                    : "bg-brand-subtle text-brand-subtle-text"
                }`}
              >
                {done ? <Check size={13} /> : i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-foreground">{t(`steps.${key}.label`)}</p>
                <p className="text-[11px] text-muted-foreground">{t(`steps.${key}.sub`)}</p>
              </div>
              {!done && <ChevronRight size={14} className="text-muted-foreground" />}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mt-1">
        <Button className="flex-1" onClick={onStart}>
          {t("steps.welcome.getStarted")}
        </Button>
        <Button variant="ghost" className="text-muted-foreground" onClick={onSkipAll}>
          {t("steps.welcome.skipAll")}
        </Button>
      </div>
    </div>
  );
}
